/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { litellmCreateInstance } from "../funcs/litellmCreateInstance.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { LitellmInstanceKeyResult } from "../models/components/litellminstancekeyresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  CreateLiteLLMInstanceRequest,
  CreateLiteLLMInstanceSecurity,
} from "../models/operations/createlitellminstance.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type CreateLiteLLMInstanceMutationVariables = {
  request: CreateLiteLLMInstanceRequest;
  security?: CreateLiteLLMInstanceSecurity | undefined;
  options?: RequestOptions;
};

export type CreateLiteLLMInstanceMutationData = LitellmInstanceKeyResult;

export type CreateLiteLLMInstanceMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * createInstance litellm
 *
 * @remarks
 * Provision a LiteLLM integration for a project and return its plaintext ingestion key once. Requires org:admin.
 */
export function useCreateLiteLLMInstanceMutation(
  options?: MutationHookOptions<
    CreateLiteLLMInstanceMutationData,
    CreateLiteLLMInstanceMutationError,
    CreateLiteLLMInstanceMutationVariables
  >,
): UseMutationResult<
  CreateLiteLLMInstanceMutationData,
  CreateLiteLLMInstanceMutationError,
  CreateLiteLLMInstanceMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildCreateLiteLLMInstanceMutation(client, options),
    ...options,
  });
}

export function mutationKeyCreateLiteLLMInstance(): MutationKey {
  return ["@gram/client", "litellm", "createInstance"];
}

export function buildCreateLiteLLMInstanceMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: CreateLiteLLMInstanceMutationVariables,
  ) => Promise<CreateLiteLLMInstanceMutationData>;
} {
  return {
    mutationKey: mutationKeyCreateLiteLLMInstance(),
    mutationFn: function createLiteLLMInstanceMutationFn({
      request,
      security,
      options,
    }): Promise<CreateLiteLLMInstanceMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(litellmCreateInstance(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
